import { useAccess } from '@umijs/max';
import { PERMISSIONS, type Permission } from '@/constants/permissions';

/**
 * Custom hook for permission checking in React components
 * Provides easy access to permission checking functions and common permission states
 */
export const usePermissions = () => {
  const access = useAccess();

  return { ...access, PERMISSIONS }
};

/**
 * Higher-order component for permission-based rendering
 * Usage: <PermissionGuard permission="MANAGE_USERS">Content</PermissionGuard>
 */
export const PermissionGuard: React.FC<{
  permission?: Permission;
  permissions?: Permission[];
  requireAll?: boolean;
  fallback?: React.ReactNode;
  children: React.ReactNode;
}> = ({
  permission,
  permissions,
  requireAll = false,
  fallback = null,
  children
}) => {
    const { hasPermission, hasAnyPermission, hasAllPermissions } = usePermissions();

    let hasAccess = false;

    if (permission) {
      hasAccess = hasPermission(permission);
    } else if (permissions && permissions.length > 0) {
      hasAccess = requireAll
        ? hasAllPermissions(permissions)
        : hasAnyPermission(permissions);
    } else {
      // No permissions specified, allow access
      hasAccess = true;
    }

    return hasAccess ? children : fallback;
  };

export default usePermissions;
